import { useCallback, useState } from "react"
import { isAxiosError } from "axios"
import { toast } from "sonner"

import {
  updateReportTemplateDefinition,
  uploadReportTemplateImage,
  type ReportTemplateDetailDto,
  type ReportTemplateFieldDto,
  type ReportTemplateFilterDto,
  type ReportTemplateLayoutDto,
} from "../../api/reportTemplateService"
import {
  normalizeLayoutForEditor,
  reindexFields,
  reindexFilters,
} from "./editorUtils"

interface UseReportTemplateEditorActionsParams {
  reportTemplateId?: string | null
  template: ReportTemplateDetailDto | null
  fields: ReportTemplateFieldDto[]
  filters: ReportTemplateFilterDto[]
  layout: ReportTemplateLayoutDto
  defaultTenantId: string
  setTemplate: (value: ReportTemplateDetailDto) => void
  setFields: (value: ReportTemplateFieldDto[]) => void
  setFilters: (value: ReportTemplateFilterDto[]) => void
  setLayout: (value: ReportTemplateLayoutDto) => void
  setSelectedWidgetId: (value: string | null) => void
  updateWidgetProps: (widgetId: string, patch: Record<string, unknown>) => void
}

export const useReportTemplateEditorActions = ({
  reportTemplateId,
  template,
  fields,
  filters,
  layout,
  defaultTenantId,
  setTemplate,
  setFields,
  setFilters,
  setLayout,
  setSelectedWidgetId,
  updateWidgetProps,
}: UseReportTemplateEditorActionsParams) => {
  const [isSaving, setIsSaving] = useState(false)
  const [uploadingImageWidgetId, setUploadingImageWidgetId] = useState<string | null>(null)

  const handleImageFileSelected = useCallback(
    async (widgetId: string, file?: File | null) => {
      if (!file) return
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file")
        return
      }

      const tenantId = String(template?.tenantId || defaultTenantId).trim()
      if (!tenantId) {
        toast.error("Missing tenant id for upload")
        return
      }

      setUploadingImageWidgetId(widgetId)
      try {
        const response = await uploadReportTemplateImage({
          tenantId,
          file,
        })
        if (!response.fileUrl?.trim()) {
          toast.error("Upload completed but file URL is empty")
          return
        }
        updateWidgetProps(widgetId, {
          bindingType: "static",
          staticUrl: response.fileUrl,
        })
        toast.success("Image uploaded")
      } catch (error) {
        console.error(error)
        if (isAxiosError(error)) {
          const message = (error.response?.data as { message?: string } | undefined)?.message
          toast.error(message || "Failed to upload image")
        } else if (error instanceof Error) {
          toast.error(error.message)
        } else {
          toast.error("Failed to upload image")
        }
      } finally {
        setUploadingImageWidgetId((current) => (current === widgetId ? null : current))
      }
    },
    [defaultTenantId, template?.tenantId, updateWidgetProps],
  )

  const handleSaveDefinition = useCallback(async () => {
    if (!reportTemplateId) return
    if (!template) {
      toast.error("Template detail is not ready")
      return
    }

    setIsSaving(true)
    try {
      const updated = await updateReportTemplateDefinition(reportTemplateId, {
        tenantId: template.tenantId || defaultTenantId,
        fields,
        filters,
        layoutJson: layout,
      })
      setTemplate(updated)
      setFields(reindexFields([...(updated.fields ?? [])].sort((a, b) => a.orderIndex - b.orderIndex)))
      setFilters(reindexFilters([...(updated.filters ?? [])].sort((a, b) => a.orderIndex - b.orderIndex)))
      setLayout(normalizeLayoutForEditor(updated.layoutJson))
      setSelectedWidgetId(null)
      toast.success("Saved report template definition")
    } catch (err) {
      console.error(err)
      if (isAxiosError(err)) {
        const message = (err.response?.data as { message?: string } | undefined)?.message
        toast.error(message || "Failed to save report definition")
      } else if (err instanceof Error) {
        toast.error(err.message)
      } else {
        toast.error("Failed to save report definition")
      }
    } finally {
      setIsSaving(false)
    }
  }, [
    defaultTenantId,
    fields,
    filters,
    layout,
    reportTemplateId,
    setFields,
    setFilters,
    setLayout,
    setSelectedWidgetId,
    setTemplate,
    template,
  ])

  return {
    isSaving,
    uploadingImageWidgetId,
    handleImageFileSelected,
    handleSaveDefinition,
  }
}
